/**
 * La consulta de las listas de Operaciones, como OPCIONES reutilizables.
 *
 * ⭐ Existe para que la RUTA y la PANTALLA compartan exactamente la misma definición (ADR-0012,
 * paso 2). El `loader` arranca la carga cuando arranca la navegación —antes de que el componente
 * exista— y la pantalla, al montar, encuentra el dato ya puesto.
 *
 * 🪤 Si la clave o el `queryFn` se escribieran dos veces, el loader calentaría una entrada que la
 * pantalla nunca lee: el usuario paga la consulta dos veces y no gana nada. Ya pasó con la precarga
 * del Inicio, y por eso Productos y esto comparten el mismo patrón.
 *
 * 🪤 Vive en su propio archivo y NO dentro de `OperationsOrdersView.tsx`: el loader lo importa de
 * forma estática, y arrastrar la vista (1.300 líneas de UI) al arranque anularía su carga diferida.
 * Acá sólo entra código de datos.
 */

import {
  fetchOrdersPage,
  enriquecerPedidos,
  type OrdersPageParams,
} from '@/lib/operations/ordersApi';
import { queryClient } from '@/lib/queryClient';
import type { OrderRecord } from '@/types/operations.types';

/**
 * Los mismos parámetros que pide `fetchOrdersPage`, con `pageSize` en vez de `limit`.
 *
 * 🪤 Se DERIVAN del tipo de la API en vez de reescribirse: si mañana `fetchOrdersPage` cambia una
 * etapa o agrega un filtro, esto deja de compilar en vez de aceptar un `string` que la BD rechaza
 * en runtime. Reescribir el tipo a mano fue el primer intento y el typecheck lo rebotó.
 */
export type ParametrosPedidosOps = Omit<OrdersPageParams, 'limit'> & { pageSize: number };

/**
 * La clave de la consulta: TODO criterio que cambie el resultado va acá.
 *
 * 🚨 FALTABAN TRES —`registraLaGuia`, `conDrop` y `createdFrom`— y el defecto era doble: no
 *    estaban en la clave (React Query servía la respuesta vieja al cambiar el filtro) Y TAMPOCO SE
 *    LE PASABAN A LA CONSULTA. O sea que el filtro «Saca la guía» y el período de los KPIs no
 *    filtraban nada: la pantalla los mandaba y esta función los tiraba en el camino.
 *
 * 🪤 Vive aparte para que la clave se escriba UNA vez. El enriquecimiento en segundo plano necesita
 *    la misma clave para escribir en la casilla correcta, y una segunda copia habría escrito en
 *    otra — el mismo defecto que ya tuvo la precarga.
 */
function clave(p: ParametrosPedidosOps) {
  return ['ops-orders', p.tenantId, p.stages.join(','), p.search,
    (p.paymentStatus ?? []).join(','), (p.shippingStatus ?? []).join(','), (p.channel ?? []).join(','),
    (p.billingStatus ?? []).join(','), p.onlyWithDue ?? false, p.soloLoPropio ?? false,
    (p.registraLaGuia ?? []).join(','), (p.modalidad ?? []).join(','), p.fechaTipo ?? '', p.fechaDesde ?? '', p.fechaHasta ?? '', p.createdFrom ?? '',
    p.sort, p.offset, p.pageSize] as const;
}

/**
 * Lo que el enriquecimiento agrega a una fila; nada mas se copia.
 * 🪤 Sin `parte` (14-oct): la parte de quien no vendió ya viaja con la fila (`fetchOrdersPage`).
 */
const CAMPOS_DE_CONTEXTO = ['transport_carrier', 'transport_agency', 'transport_type', 'transport_logo_url', 'customer_orders_count', 'lines'] as const;

/** Lo que pone cada adorno en la fila (`attachTransport`, `attachCustomerStats`); las miniaturas van por línea. */
const CAMPOS_QUE_PONEN_LOS_ADORNOS = ['transport_type', 'transport_logo_url', 'customer_orders_count'] as const;

/**
 * Un refetch trae las filas SIN su contexto: mientras viaja el enriquecimiento nuevo, cada fila conserva el que ya
 * tenía la casilla (por id).
 *
 * 🚨 «COMO QUE SE RECARGA» (founder, 14-oct). La lista se vuelve a pedir al volver a la pestaña, a los 30 s y con
 *    cada aviso en vivo, y cada vez la casilla recibía las filas peladas: el tipo y el logo del transportista, los
 *    pedidos del cliente y las miniaturas desaparecían un instante y volvían.
 *
 * 🪤 SÓLO LO QUE FALTA, Y SÓLO LO QUE PONEN LOS ADORNOS: si la respuesta nueva ya trae un dato, manda el nuevo. Y
 *    las miniaturas se pasan por PRODUCTO —la foto es del producto (`attachLineImages`)—, no las líneas enteras:
 *    si alguien cambió un producto, la fila muestra el producto nuevo, y sin la foto del viejo.
 */
export function conservarElContexto(nuevas: OrderRecord[], viejas: OrderRecord[] | undefined): OrderRecord[] {
  if (!viejas?.length) return nuevas;
  const porId = new Map(viejas.map((o) => [o.id, o]));
  return nuevas.map((fila) => {
    const vieja = porId.get(fila.id);
    if (!vieja) return fila;
    const contexto: Partial<OrderRecord> = {};
    for (const k of CAMPOS_QUE_PONEN_LOS_ADORNOS) {
      if (fila[k] === undefined && vieja[k] !== undefined) (contexto as Record<string, unknown>)[k] = vieja[k];
    }
    const miniaturas = new Map((vieja.lines ?? []).filter((l) => l.product_id && l.image_url !== undefined).map((l) => [l.product_id, l.image_url]));
    const lines = fila.lines?.map((l) => (l.product_id && l.image_url === undefined && miniaturas.has(l.product_id) ? { ...l, image_url: miniaturas.get(l.product_id) } : l));
    return Object.keys(contexto).length || lines?.some((l, i) => l !== fila.lines?.[i]) ? { ...fila, ...contexto, ...(lines ? { lines } : {}) } : fila;
  });
}

/** Mezcla el contexto de `enriquecidos` sobre las filas de `prev`, por id. Sin coincidencias, devuelve `prev` tal cual. */
export function mezclarContexto<T extends { items: OrderRecord[] }>(prev: T | undefined, enriquecidos: OrderRecord[]): T | undefined {
  if (!prev) return prev;
  const porId = new Map(enriquecidos.map((o) => [o.id, o]));
  let cambio = false;
  const items = prev.items.map((fila) => {
    const e = porId.get(fila.id);
    if (!e) return fila;
    cambio = true;
    const contexto: Partial<OrderRecord> = {};
    for (const k of CAMPOS_DE_CONTEXTO) (contexto as Record<string, unknown>)[k] = e[k];
    return { ...fila, ...contexto };
  });
  return cambio ? { ...prev, items } : prev;
}

export function opcionesPedidosOps(p: ParametrosPedidosOps) {
  return {
    /*
     * La clave ES la identidad de la consulta: todo criterio que cambie el resultado va acá.
     *
     * 🚨 Y FALTABAN TRES. `registraLaGuia` (el filtro «Saca la guía»), `conDrop` y `createdFrom`
     *    (el período de los KPIs) cambian las filas que vuelven y NO estaban en la clave: React
     *    Query veía la misma consulta, servía la respuesta vieja y no volvía a pedir nada. El
     *    usuario tocaba el filtro y la lista se quedaba igual.
     *
     * 🪤 Es el mismo defecto que arriba se describe y abajo se incumplía. Cuando se agregue un
     *    filtro nuevo, su lugar es esta línea — no la de al lado.
     */
    queryKey: clave(p),
    staleTime: 30_000,
    /*
     * ⭐ LAS FILAS PRIMERO, EL CONTEXTO DESPUES. La consulta devuelve la pagina apenas llega y el
     *    enriquecimiento —transportista, pedidos del cliente, miniaturas— viaja aparte y actualiza
     *    la casilla cuando termina. Antes esas tres consultas se esperaban: ~165 ms mirando un
     *    cargando con las filas ya recibidas.
     *
     * 🪤 SE ESCRIBE CON `setQueryData` Y NO MUTANDO LAS FILAS. Mutar los objetos en su lugar deja
     *    la referencia igual, React no ve cambio y el enriquecimiento no aparece nunca — un bug que
     *    se ve como "a veces no carga el transportista".
     *
     * 🪤 Y SE MEZCLA FILA POR FILA, POR `id`, sobre lo que la casilla tenga en ese momento. Antes se
     *    exigia `prev.items === pagina.items` (la MISMA referencia) para no pisar otra pagina. Pero
     *    React Query comparte estructura entre respuestas: en cada refetch —el foco, los 30 s, un
     *    aviso en vivo— la casilla recibe un arreglo NUEVO con las filas sin contexto, la referencia
     *    ya no coincide y el enriquecimiento se descartaba entero. El founder (22-sep-2026), con la
     *    lista de Vitalex: «no se ve ni transporte ni transportista» — todo en «—» salvo Falabella,
     *    que no depende del envio. Mezclar por id sirve a la pagina correcta y sobrevive al refetch;
     *    si el usuario cambio de pagina, ningun id coincide y no se toca nada.
     *
     * 🪤 SOLO EL CONTEXTO, no la fila entera: la copia enriquecida es de ESTA respuesta, y si un
     *    refetch posterior ya cambio el estado de pago de una fila, pisarla con la copia vieja seria
     *    retroceder el dato que se vino a ver.
     */
    queryFn: async () => {
      const pagina = await fetchOrdersPage({
      tenantId: p.tenantId,
      stages: p.stages,
      search: p.search,
      paymentStatus: p.paymentStatus,
      shippingStatus: p.shippingStatus,
      channel: p.channel,
      billingStatus: p.billingStatus,
      onlyWithDue: p.onlyWithDue,
      // 🚨 Estos tres se perdían acá. Ver `clave`.
      registraLaGuia: p.registraLaGuia,
      modalidad: p.modalidad, fechaTipo: p.fechaTipo, fechaDesde: p.fechaDesde, fechaHasta: p.fechaHasta,
      createdFrom: p.createdFrom,
      sort: p.sort,
      offset: p.offset,
      limit: p.pageSize,
      });
      if (pagina.items.length > 0) {
        void enriquecerPedidos(pagina.items, p.tenantId)
          .then((enriquecidos) => {
            queryClient.setQueryData(clave(p), (prev: typeof pagina | undefined) => mezclarContexto(prev, enriquecidos));
          })
          .catch(() => { /* sin contexto la lista sigue siendo util */ });
      }
      // En un refetch, lo que ya se veía no se apaga mientras llega lo nuevo: ver `conservarElContexto`.
      const previa = queryClient.getQueryData<typeof pagina>(clave(p));
      return { ...pagina, items: conservarElContexto(pagina.items, previa?.items) };
    },
  };
}
